import styled from '@emotion/styled';
import React from 'react';
import { useTransition } from 'transition-hook';

import usePreventScroll from '../../hooks/use-prevent-scroll';
import type { LensDivProps } from '../../types';
import { getColorValue } from '../../utilities';

const duration = 300;

const BackdropWrapper = styled.div<BackdropProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: calc(100vh - calc(100vh - 100%));
  height: 100dvh;
  height: -webkit-fill-available;
  background: ${props => getColorValue(props.backgroundColor)};
  z-index: ${props => props.zIndex};
  overflow: hidden;
`;

const ChildrenWrapper = styled.div`
  overflow: auto;
  height: 100%;
`;

const Backdrop = React.forwardRef<
  HTMLDivElement,
  BackdropProps &
    React.ComponentProps<typeof BackdropWrapper> &
    Omit<
      LensDivProps,
      keyof BackdropProps & React.ComponentProps<typeof BackdropWrapper>
    >
>(
  (
    {
      children,
      isOpen,
      zIndex = 1000,
      backgroundColor = 'backdropDark',
      ...props
    },
    ref
  ) => {
    const { stage, shouldMount } = useTransition(isOpen, duration);

    usePreventScroll('html', isOpen);

    return (
      <>
        {shouldMount && (
          <BackdropWrapper
            ref={ref}
            backgroundColor={backgroundColor}
            zIndex={zIndex}
            style={{
              transition: `opacity ${duration}ms`,
              opacity: stage === 'enter' ? 1 : 0,
            }}
            {...props}
          >
            <ChildrenWrapper>{children}</ChildrenWrapper>
          </BackdropWrapper>
        )}
      </>
    );
  }
);

Backdrop.displayName = 'Backdrop';

type BackdropProps = {
  isOpen?: boolean;
  children?: React.ReactNode;
  zIndex?: number;
  backgroundColor?: string;
};

export default Backdrop;
